import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './RecaptchaBox.css';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

let scriptPromise = null;

const loadScript = () => {
  if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
    return Promise.resolve();
  }

  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');

    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
};

// render=explicit does not guarantee grecaptcha.render exists the moment the
// script fires onload, so poll briefly for it.
const waitForApi = () =>
  new Promise((resolve, reject) => {
    let attempts = 0;
    const tick = () => {
      if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
        resolve();
        return;
      }
      attempts += 1;
      if (attempts > 100) {
        reject(new Error('reCAPTCHA API never became ready'));
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });

const RecaptchaBox = forwardRef(({ onChange, error }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(widgetIdRef.current);
          onChangeRef.current('');
        }
      },
    }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const element = containerRef.current;

    if (!SITE_KEY) {
      setLoadError('reCAPTCHA is not configured. Please contact the temple office.');
      return undefined;
    }

    loadScript()
      .then(waitForApi)
      .then(() => {
        // StrictMode runs effects twice in development; only render once.
        if (cancelled || !element || widgetIdRef.current !== null) return;

        element.innerHTML = '';
        widgetIdRef.current = window.grecaptcha.render(element, {
          sitekey: SITE_KEY,
          callback: (token) => onChangeRef.current(token || ''),
          'expired-callback': () => onChangeRef.current(''),
          'error-callback': () => onChangeRef.current(''),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Could not load reCAPTCHA. Please refresh the page and try again.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="yk-recaptcha">
      <div className="yk-recaptcha-widget" ref={containerRef} />

      {loadError && <span className="yk-recaptcha-error">{loadError}</span>}
      {!loadError && error && <span className="yk-recaptcha-error">{error}</span>}
    </div>
  );
});

RecaptchaBox.displayName = 'RecaptchaBox';

export default RecaptchaBox;
